import { nonnull } from "@tento-core/assert";
import "@tento-ui/components/home-card/row-icon";

// Sticky legend labels — how #content-area prevents overlap
//
// std-sheet-legend-default-row uses `position: sticky` to pin row labels
// while scrolling. Without containment, the sticky label's containing block
// is the full Nested #root (height = cellHeightWithChildrenPx), so the label
// sticks through the entire row including children — overlapping them.
//
// Fix: #content-area wraps <slot name="content"> with height = cellHeightPx
// (the row's own content height, excluding children). This becomes the
// containing block for sticky, so the label naturally unsticks at the
// children boundary. No margin-bottom hacks needed.
//
// Full-height legends (e.g. std-data-legend, lona-events-legend) overflow
// #content-area visually (overflow: visible) but don't affect flex layout —
// children are positioned after #content-area in the flex column.


import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { Point } from "@tento-core/point";
import { LoadingIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Avatar } from "@tento-ui/components/avatar";
import { type Option, type SurfaceKind, Timescale } from "@tento-lona/sheets";
import { RowIcon } from "@tento-ui/components/home-card/row-icon";
import type { RowLocalId } from "@tento-lona/sheets";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { SheetViewModel, type SheetRow } from "@tento-lona";
import { RootNode } from "@tento-lona/platform";
import { DefaultLegendLayout, type TextMeasurer } from "./default-legend-layout.ts";
import {
  DefaultBinderHelpers,
} from "./binder/default-binder-helpers.ts";
import type { SheetBinder } from "./sheet-index-renderer.ts";

/** Called when a row's label is clicked. */
export type RowLabelClickHandler = (
  $label: HTMLElement,
  row: SheetRow.Joined.Handle,
) => void;

/**
 * Find the unique top-level surface anchor row of the given
 * kind (`"header"` / `"footer"`). Returns `null` when no such
 * row exists. Two shapes are recognized: server-backed
 * surface-containers (`descriptor.kind === "surface-container"`,
 * kind from `metadata.surface`) and authored surface region
 * rows (`descriptor.kind === "surface"`, kind from
 * `attributes.surface` — bare/body surfaces don't match). The
 * kind comes from `row.surface` (which walks up to the nearest
 * surface ancestor — for a top-level anchor that's itself).
 * Used by `SheetLegend` to render the surface's children
 * directly without producing a legend entry for the transparent
 * surface row itself.
 */
function findSurface(
  topLevelRows: readonly SheetRow.Joined.Handle[],
  kind: SurfaceKind,
): Option<SheetRow.Joined.Handle> {
  for (const row of topLevelRows) {
    const desc = row.descriptor;
    if (desc.role !== "inert") continue;
    if (desc.kind !== "surface-container" && desc.kind !== "surface") continue;
    if (row.surface === kind) return row;
  }
  return null;
}

/** A sheet's legend: the column of row labels beside its cells. */
@component({ name: "std-sheet-legend" })
export class SheetLegend extends LonaWebComponent {
  isFrozen: boolean = false;
  isFooter: boolean = false;

  $children: Map<RowLocalId, SheetLegend.Nested> = new Map();

  bindNullState() {
    this.clearChildren();
    this.$("root").toggleAttribute("empty", true);
  }

  bindNow(
    rowList: SheetViewModel.Surface<SheetBinder>,
    handle: SheetDelegate,
    activeRowOptions: LiveData.Readable<Option<RowLocalId>>,
    section: "header" | "other",
    resizeDelegate: SheetLegend.ResizeDelegate,
    outPromises: Promise<unknown>[],
  ) {
    const isHeader = section === "header";
    // Surface anchor rows (server-backed surface-containers and
    // authored surface[header|footer] regions) are transparent
    // structural wrappers — they should not produce their own
    // legend entry. The header / footer legend slots render the
    // anchor's children directly via the body-view-tree
    // (`tree.viewChildren`) so projection-hoisted content (e.g.
    // the calendar template's all-day timeline row) is included
    // alongside the authored children. Body slot renders
    // top-level rows that are NOT inside a surface anchor.
    // Body is implicit: top-level rows outside a header/footer anchor.
    const allChildren = rowList.joinedTree.children(RootNode);
    const viewRoots = rowList.joinedTree.viewChildren(null);
    const headerSurface = findSurface(allChildren, "header");
    const footerSurface = findSurface(allChildren, "footer");
    let rows: SheetRow.Joined.Handle[];
    if (this.isFooter) {
      rows = footerSurface
        ? rowList.joinedTree.viewChildren(footerSurface.id).filter((c) => !c.subsumed)
        : allChildren.filter((r) => r.surface === "footer");
    } else if (this.isFrozen) {
      // Every sheet has a header surface anchor (at minimum the
      // surface-container synthesized in SURFACES_STATIC,
      // system-rows.ts); the header slot always renders its
      // viewChildren.
      rows = headerSurface
        ? rowList.joinedTree.viewChildren(headerSurface.id).filter((c) => !c.subsumed)
        : [];
    } else {
      rows = viewRoots.filter((r) =>
        r.surface !== "footer" &&
        r !== headerSurface &&
        r !== footerSurface &&
        !r.subsumed
      );
    }
    // Deferred to here (rather than at entry) so the one dev line carries the
    // resolved row count — the number that decides the visibility latch below.
    Tracing.dev("sheet/legend/bind, header =", isHeader, "rows =", rows.length);

    this.toggleAttribute("frozen", this.isFrozen);
    if (rows.length === 0) {
      // An empty legend latches hidden until a later bindNow yields rows; for
      // the frozen header this is a blank-by-construction band (vs a dropped bind).
      Tracing.log("header/legend-latched-hidden", {
        isHeader,
        isFrozen: this.isFrozen,
        rowsLen: rows.length,
        surfaceFound: (this.isFooter ? footerSurface : headerSurface) != null,
      });
    }
    this.style.visibility = rows.length === 0 ? "hidden" : "visible";
    const len = rows.length;

    const rendered = new Set();
    let $prev: Option<Element> = null;
    for (const [idx, row] of rows.entries()) {
      rendered.add(row.id);
      const existing = this.$children.get(row.id);
      const $child = existing ?? SheetLegend.Nested.make();

      $child.bindNow(
        handle,
        rowList,
        row,
        0,
        activeRowOptions,
        section,
        resizeDelegate,
        outPromises,
      );
      $child.style.zIndex = String(len - idx + 1);
      this.$children.set(row.id, $child);

      // Insert only when the frame is not already in position: the
      // unconditional appendChild MOVED every attached frame on every pass —
      // a detach/reattach per row per rail (connected callbacks, style
      // invalidation, layout), the measured bulk of the legend bindNow.
      // When order is unchanged (the common pass) this whole loop is
      // DOM-neutral; a genuine reorder degrades to the same insert it always
      // did.
      const inPosition = $child.parentNode === this &&
        ($prev == null
          ? this.firstElementChild === $child
          : $child.previousElementSibling === $prev);
      if (!inPosition) {
        this.insertBefore(
          $child,
          $prev == null ? this.firstChild : $prev.nextSibling,
        );
      }
      $prev = $child;
    }
    for (const [c, $e] of this.$children) {
      if (rendered.has(c)) continue;
      this.$children.delete(c);
      DomUtils.removeFromDom($e);
    }

    this.$("root").toggleAttribute("empty", rows.length === 0);
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        z-index: 0;
      }

      #root::after {
        position: absolute;
        content: "";
        right: -0.5px;
        width: 1px;
        top: var(--divider-inset-top, 0px);
        bottom: var(--divider-inset-bottom, 0px);
        background: var(--legend-right-divider-bg, var(--divider));
      }

      #root {
        position: relative;
        background: var(--sheet-legend-bg-color, var(--bg));
        padding-left: 8px;
        align-items: stretch;
        height: 100%;
        width: 100%;
      }

      :host([frozen]) #root {
        padding-bottom: 1px;
      }

      #root[empty] {
        border-right: none;
      }

      slot::slotted(*) {
        opacity: var(--opacity);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <slot></slot>
    </std-col>
  `;
}

/** Types and values that go with `SheetLegend`: `ResizeDelegate`, `Nested`, `DefaultRow`. */
export namespace SheetLegend {
  export type ResizeDelegate = {
    onTryResize(row: SheetRow.Joined.Handle, n: number): number;
    onBeginDragResize(): void;
    onEndDragResize(): void;
  };

  @component({ name: "std-sheet-legend-nested" })
  export class Nested extends LonaWebComponent {
    $content: Option<HTMLElement> = null;
    $children: Map<RowLocalId, SheetLegend.Nested> = new Map();
    #activeRowOptions: Option<LiveData.Readable<Option<RowLocalId>>> = null;

    bindNow(
      handle: SheetDelegate,
      sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      depth: number,
      activeRowOptions: LiveData.Readable<Option<RowLocalId>>,
      section: "header" | "other",
      resizeDelegate: SheetLegend.ResizeDelegate,
      outPromises: Promise<unknown>[],
    ): void {
      const isHeader = section === "header";
      const prevRowId = this.getAttribute("row-id");
      this.setAttribute("row-id", row.id.toString());

      // Set ARIA rowheader role and label for screen reader accessibility
      this.setAttribute("role", "rowheader");
      const ariaLabel = row.label ?? row.labelPlaceholder;
      this.setAttribute("aria-label", ariaLabel);

      // The collapsed rail kills every legend child it does not recognise
      // (`Sheet`'s `#sheet-legend > :not([condensed-capable])`). This frame
      // is the child in that rule, so a legend that supports the condensed
      // projection has to have its declaration REACHED here. Cleared on every
      // bind and re-declared below from the freshly bound content, so it is
      // always a statement about what this frame currently holds — never
      // about collapse state, which this component never reads. It is also
      // why an early return (`hidden` row, `hideLegend`) leaves it off: a
      // frame with no content declares nothing.
      this.toggleAttribute("condensed-capable", false);

      const clear = (clearContent: boolean = false) => {
        for (const c of [...this.children]) {
          if (!(c instanceof HTMLElement)) continue;
          if (c.slot === "content") continue;
          DomUtils.removeFromDom(c);
        }
        this.$children.clear();
        if (clearContent && this.$content) {
          DomUtils.removeFromDom(this.$content);
          this.$content = null;
        }
      };

      if (prevRowId !== row.id.toString()) clear(true);

      const layout = sheet.layout.rowLayout.get(row.id);
      this.toggleAttribute("hidden", layout == null);
      if (!layout) return clear(true);

      // 1) Root Styling
      {
        this.toggleAttribute("hide-divider", row.isSystemRow ?? false);
        this.$("root").assignStyles({
          marginLeft: depth > 0 ? "20px" : "0px",
        });
        this.style.setProperty("--depth", String(depth));
        this.assignStyles({
          "--height": layout.cellHeightWithChildrenPx + "px",
          "--content-height": layout.cellHeightPx + "px",
          "--label-height":
            Math.max(layout.options?.labelHeightPx ?? 32, 32) + "px",
        });
        const resizable = row.descriptor.heightBounds != null;
        this.toggleAttribute("resizable", resizable);
        if (resizable) {
          // For span-stacked rows, synthesize stackingInfo so the drag handler
          // divides total height by spanCount to get the per-subcell heightPx.
          let effectiveStackingInfo = layout.stackingInfo;
          const spec = row.layoutSpec(sheet.timescale);
          if (
            !effectiveStackingInfo &&
            spec.minWidthTimescale &&
            Timescale.shouldSpan(spec.minWidthTimescale, sheet.timescale) &&
            !Timescale.shouldSpan(row.timescaleSetting, sheet.timescale)
          ) {
            const spanCount = Timescale.conservativeSpanCount(
              spec.minWidthTimescale,
              sheet.timescale,
            );
            effectiveStackingInfo = {
              cellsPerColumn: spanCount,
              individualCellHeight: layout.cellHeightPx / spanCount,
              visibleCellCount: spanCount,
              seeMoreHeight: 0,
            };
          }
          this.bindDragHandle(
            layout.cellHeightWithChildrenPx,
            row,
            resizeDelegate,
            effectiveStackingInfo,
          );
        }
      }

      // 2) Content Row
      if (row.hideLegend) return clear(true);
      const legendFactory =
        handle.pluginManager.get(row).plugin().legendFactory ??
        SheetLegend.DefaultFactory;
      const $content = (this.$content = this.$content ?? legendFactory.make());
      {
        if (!$content) return clear(true);
        const p = legendFactory.bind($content, row, handle, sheet.timescale);
        if (p && p instanceof Promise) outPromises.push(p);
        // Remove listener from previous activeRowOptions before adding new one
        this.#activeRowOptions?.removeListener(this);
        this.#activeRowOptions = activeRowOptions;
        activeRowOptions.addListener(this, (rid) => {
          $content.toggleAttribute("selected", row.id === rid && rid != null);
        });
        $content.slot = "content";
        // Same-instance content is kept across binds (`this.$content ??`);
        // re-appending it every pass detached/reattached it for nothing.
        if ($content.parentElement !== this) this.appendChild($content);
        // Mirror the legend's OWN declaration onto the frame (see the reset
        // above). The legend component sets `condensed-capable` on itself
        // during its bind — `SheetLegend.DefaultRow` and `DataLegend` do
        // today — because only it knows whether its parts can survive a 64px
        // lane; this frame just carries that word out to where the rail's
        // kill rule can read it. Read after `bind` for exactly that reason.
        // A legend that binds asynchronously has to declare synchronously to
        // be seen here, which is the intended contract: capability is a
        // property of the component, not of its data.
        this.toggleAttribute(
          "condensed-capable",
          $content.hasAttribute("condensed-capable"),
        );
      }

      // 3) Children — body-view-tree-aware so projection-hoisted
      // rows render as LEAVES (no nested legend entries for their
      // authored children, which the parent's plugin paints
      // directly). E.g. the calendar template's hoisted
      // `timeline[row]` carries a `calendar-list` child in the
      // authored tree but is a leaf in the view tree; without this
      // the legend would render both `timeline[row]` AND
      // `calendar-list` as separate sticky entries.
      if (!row.rendersChildren) return clear();
      const children = sheet.joinedTree
        .viewChildren(row.id)
        .filter((c) => !c.subsumed && sheet.layout.rowLayout.has(c.id));
      if (children.length <= 0) return clear();

      const rendered = new Set();
      for (const child of children) {
        rendered.add(child.id);

        const layout = sheet.layout.rowLayout.get(child.id);
        const $child = this.$children.get(child.id) ?? Nested.make();
        $child.bindNow(
          handle,
          sheet,
          child,
          depth + 1,
          activeRowOptions,
          section,
          resizeDelegate,
          outPromises,
        );
        if (layout) {
          $child.style.setProperty(
            "--sticky-top",
            isHeader ? layout.offsetPx + 0 + "px" : "",
          );
          $child.style.setProperty(
            "--row-option-sticky-top",
            layout.offsetPx + 0 + "px",
          );
        }
        if ($child.parentElement !== this) {
          this.appendChild($child);
        }
        this.$children.set(child.id, $child);
      }

      for (const [c, $e] of this.$children) {
        if (rendered.has(c)) continue;
        this.$children.delete(c);
        DomUtils.removeFromDom($e);
      }
    }

    disconnectedCallback() {
      this.#activeRowOptions?.removeListener(this);
      // Note: Don't clear drag handlers here - the element may be getting moved
      // in the DOM during rebind, not truly removed. The handlers will be
      // re-registered by bindDragHandle if the element is reconnected.
    }

    onRecycle() {
      // Clear JS fields that survive _recycle (which only resets DOM/attributes).
      // Without this, a pooled Nested retains a stale $content reference that
      // points to a TimelineColumnLegend now owned by a different Nested. When the
      // stale Nested is reused for another row, clear(true) would yank the
      // TimelineColumnLegend from its real owner via DomUtils.removeFromDom.
      this.$content = null;
      this.$children.clear();
      this.#activeRowOptions = null;
    }

    bindDragHandle(
      initialCellHeight: number,
      row: SheetRow.Joined.Handle,
      resizeDelegate: SheetLegend.ResizeDelegate,
      stackingInfo: Option<SheetCell.Layout["stackingInfo"]>,
    ) {
      let pointerId: Option<number>;
      let heightPx = initialCellHeight;

      GESTURE_MANAGER.addDragGesture(this.$("drag-handle"), `drag-${row.id}`, {
        onPointerDown: (e) => {
          pointerId = e.pointerId;
          this.$("drag-handle").setPointerCapture(pointerId);
          this.toggleAttribute("resizing", true);
          resizeDelegate.onBeginDragResize();
        },
        onPointerMove: (e: { progress: Point }): void => {
          const stackH = heightPx - e.progress.y;
          const individualH = SheetCell.Layout.toIndividualHeight(
            stackH,
            stackingInfo,
          );
          const resizedIndividual = resizeDelegate.onTryResize(
            row,
            individualH,
          );
          if (!resizedIndividual) return;
          const resizedStack = SheetCell.Layout.toStackHeight(
            resizedIndividual,
            stackingInfo,
          );
          this.style.setProperty("--height", resizedStack + "px");
        },
        onPointerUp: (e: { progress: Point }) => {
          const stackH = heightPx - e.progress.y;
          const individualH = SheetCell.Layout.toIndividualHeight(
            stackH,
            stackingInfo,
          );
          const actualIndividual = resizeDelegate.onTryResize(row, individualH);
          const actualStack = SheetCell.Layout.toStackHeight(
            actualIndividual,
            stackingInfo,
          );
          this.style.setProperty("--height", actualStack + "px");
          heightPx = actualStack;
          this.$("drag-handle").releasePointerCapture(nonnull(pointerId, "a resize ends the drag it began"));
          this.toggleAttribute("resizing", false);
          resizeDelegate.onEndDragResize();
        },
      });
    }

    static $styles: Style.Compat[] = [
      css`
        :host {
          height: var(--height);
          min-width: max-content;
          width: 100%;
        }

        /* Same declaration, one exit. A frame that declared a condensed
           projection (see bindNow) lets the collapsed rail relax the
           max-content floor to 0, so the frame becomes exactly the lane and
           right-anchored content inside it — the chart row's y-axis — lands
           in the lane instead of parking past its right edge and being
           clipped away. The fallback IS max-content, so an expanded rail and
           every frame that declared nothing are unchanged. The floor moving
           does NOT reflow the row: the parts that must keep their expanded
           metrics ask for max-content themselves and overflow, which is what
           the clip rule on the rail has always been for. */
        :host([condensed-capable]) {
          min-width: var(--legend-row-frame-min-width, max-content);
        }

        #root {
          position: relative;
          height: calc(var(--height));
        }

        #root::before {
          position: absolute;
          content: "";
          top: 34px;
          left: 10px;
          width: 3px;
          border-radius: 2px;
          background: var(--divider);
          height: calc(100% - var(--label-height) - 4px);
          /* The spine is depth chrome for a tree the condensed lane does not
             show — a 64px lane holding a name and an axis has no hierarchy to
             draw. It fades with the rest of the dead parts (published by the
             rail, 1 when expanded). Decorative, so no pointer pairing. */
          opacity: var(--legend-row-spine-opacity, 1);
        }

        :host([hide-divider]) #root::before {
          display: none;
        }

        #root::after {
          position: absolute;
          content: "";
          right: 0px;
          top: 0px;
          width: 8px;
        }

        :host([resizable]) #drag-handle {
          pointer-events: all;
        }


        #drag-handle {
          position: absolute;
          bottom: -1px;
          left: 0px;
          width: 100%;
          height: 6px;
          bottom: calc(0px - 2px);
          cursor: ns-resize;
          transition: opacity 0.3s ease;
          pointer-events: none;
          opacity: 0;
          z-index: 99;
        }

        #drag-handle-line {
          background: var(--fg-icon-ter);
          border-radius: 2px;
          margin-left: 24px;
          width: calc(100% - 24px - 12px);
          margin-top: 1px;
          height: 4px;
        }

        :host([resizing]) #drag-handle,
        #drag-handle:hover {
          opacity: 1;
        }

        #content-area {
          height: var(--content-height);
          overflow: visible;
        }
      `,
    ];

    static $html: Option<HTMLTemplateElement> = html`
      <std-col id="root">
        <div id="content-area">
          <slot name="content"></slot>
        </div>
        <slot></slot>
        <div id=drag-handle>
          <div id=drag-handle-line></div>
        </div>
      </std-col>
    `;
  }

  @component({
    name: "std-sheet-legend-default-row",
  })
  export class DefaultRow extends LonaWebComponent {
    $icon: RowIcon = this.hydrate("icon");
    #$loadingIcon: Option<HTMLElement> = null;
    #$timescaleIndicator: Option<Avatar> = null;

    static bind(
      $legend: DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
      sheetTimescale: Timescale,
    ): DefaultRow {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
        { sheetTimescale },
      );
      return $legend;
    }

    bind(
      row: SheetRow.Joined.Handle,
      onClick: (target: "default" | "emoji") => void,
      handle: SheetDelegate,
      options?: {
        defaultLabel?: string;
        sheetTimescale?: Timescale;
      },
    ) {
      this.setAttribute("row-id", row.id.toString());
      this.clearChildren();

      // Self-declaration for the collapsed rail (Sheet's `legend-collapsed`
      // block). This row HAS a condensed projection: the name survives, the
      // icon and the timescale badge fade out, and the label slides into the
      // icon's place — all of it in the CSS below, driven by the
      // `--legend-row-*` tokens the rail publishes. Stated unconditionally
      // because it is a property of the component, not of this row's data;
      // `SheetLegend.Nested` mirrors it onto the frame the rail's kill rule
      // actually selects. Re-set on every bind, which is also what restores
      // it after pooling — `_recycle` strips every attribute.
      this.toggleAttribute("condensed-capable", true);

      this.toggleAttribute("disabled", row.isSystemRow ?? false);

      this.$icon.bind(row, { icon: handle.pluginManager.get(row).getIcon() });
      this.$icon.addPointerEvent({
        onClick: () => onClick("emoji"),
        onContextMenu: () => onClick("emoji"),
      });

      this.$("label").textContent =
        row.label ?? options?.defaultLabel ?? row.labelPlaceholder;
      GESTURE_MANAGER.addPointerEvent(this.$("label-container"), {
        onClick: () => onClick("default"),
        onContextMenu: () => onClick("default"),
      });

      // Check sync state and show/hide loading indicator
      const syncState = handle.state.peekSyncState?.(row.id);
      const isSyncing =
        syncState != null &&
        (syncState.status === "pending" || syncState.status === "running");
      // Timescale indicator — suppress during rebind to prevent flicker.
      // The badge is hidden immediately and revealed in a fastdom mutate
      // which batches with the sentinel's width application, so the badge
      // only appears after the legend column width is stable.
      const timescaleInfo = this.#getTimescaleInfo(row.timescaleSetting);
      const sheetTimescale = options?.sheetTimescale;
      if (timescaleInfo) {
        if (!this.#$timescaleIndicator) {
          this.#$timescaleIndicator = Avatar.make();
          this.#$timescaleIndicator.id = "timescale-indicator";
        }
        this.#$timescaleIndicator.bindInitials({
          initials: timescaleInfo.abbrev,
          backgroundColor: "var(--bg-surface-light)",
          color: "var(--text-pri)",
          size: "20",
          tooltip: timescaleInfo.label,
          tooltipPosition: "right",
          shadow: null,
        });
        const differs =
          sheetTimescale != null && sheetTimescale !== row.timescaleSetting;
        // Hide immediately so the badge isn't visible during layout flux
        this.#$timescaleIndicator.removeAttribute("visible");
        this.$("root").appendChild(this.#$timescaleIndicator);
        // Reveal after layout settles (batches with sentinel width apply)
        if (differs) {
          const $ts = this.#$timescaleIndicator;
          $$.mutate(() => $ts.toggleAttribute("visible", true));
        }
      } else if (this.#$timescaleIndicator) {
        DomUtils.removeFromDom(this.#$timescaleIndicator);
        this.#$timescaleIndicator = null;
      }

      if (isSyncing) {
        if (!this.#$loadingIcon) {
          // `LonaIcons.make` is declared `any`, so assigning it straight
          // to the field re-widens it to `Option<HTMLElement>`. Bind a
          // typed local first, the way the `Avatar.make()` branch above
          // gets for free.
          const $icon: HTMLElement = LonaIcons.make(LoadingIcon, {
            sizePx: 16,
          });
          $icon.id = "sync-indicator";
          this.#$loadingIcon = $icon;
        }
        this.$("root").appendChild(this.#$loadingIcon);
      } else if (this.#$loadingIcon) {
        DomUtils.removeFromDom(this.#$loadingIcon);
        this.#$loadingIcon = null;
      }
    }

    #getTimescaleInfo(
      timescale: Timescale | "custom",
    ): Option<{ abbrev: string; label: string }> {
      switch (timescale) {
        case "day":
          return { abbrev: "D", label: "Day" };
        case "week":
          return { abbrev: "W", label: "Week" };
        case "month":
          return { abbrev: "M", label: "Month" };
        case "year":
          return { abbrev: "Y", label: "Year" };
        default:
          return null; // "custom" shows no indicator
      }
    }

    onRecycle() {
      // These elements live in shadow $("root"), which survives _recycle().
      // Must remove from DOM before nulling refs, otherwise next bind()
      // creates duplicates (zombie badges).
      this.#$timescaleIndicator?.remove();
      this.#$timescaleIndicator = null;
      this.#$loadingIcon?.remove();
      this.#$loadingIcon = null;
    }

    static $labelContainerStyle: Style.Compat = css`
      #label {
        color: var(--text-sec);
        --margin-top: 1px;
      }

      #label-container {
        cursor: pointer;
        position: relative;
        height: var(--label-height, 32px);
        flex-grow: 1;
        /* 0 normally — the label is the part that yields when the rail is
           tight. In the condensed projection it is the opposite: the name is
           the only mark this row keeps, so it asks for max-content and
           OVERFLOWS the frame the rail shrank to the lane, exactly as rows
           have always overflowed the collapsed rail. Without this the name
           would reflow into whatever the lane left over (measured: 19px for
           "memory") instead of being clipped at the lane's edge. */
        min-width: var(--legend-row-label-min-width, 0);
        overflow: hidden;
        align-items: center;
        padding-inline: 8px;
        /* 1px normally. In the collapsed rail's condensed projection the
           name is the only mark this row keeps, so it slides into the faded
           icon's place: the rail publishes --legend-row-label-shift (-32px)
           and the label ink lands on the rail's own 8px pad instead of 41px
           in. The fallback IS the expanded value, so an un-collapsed rail —
           and every consumer that never publishes the token, e.g.
           all-day-events/legend.ts reusing this stylesheet — is unchanged. */
        margin-left: var(--legend-row-label-shift, 1px);
      }

      :host([selected]) #label-container::before,
      #label-container:hover::before {
        content: "";
        position: absolute;
        left: 0px;
        top: 2px;
        bottom: 2px;
        right: 0px;
        border-radius: 4px;
        background: var(--bg-hover);
        z-index: 0;
      }

      :host([disabled]) #label-container {
        pointer-events: none;
        display: none;
      }

      #icon {
        min-height: calc(var(--label-height, 32px) - 4px);
        background: var(--std-calendar-legend-background-color);
        /* Condensed projection: the icon is the part the name replaces, so
           it fades. The pointer token is NOT decoration — this icon opens
           the row-options popover on click, and an invisible 24px target
           sitting under the collapsed name would swallow the click that
           belongs to the label. Opacity and hit-testing are separate
           declarations because CSS cannot derive one from the other. Both
           fall back to the expanded value when nothing publishes them. */
        opacity: var(--legend-row-icon-opacity, 1);
        pointer-events: var(--legend-row-icon-pointer, auto);
      }
    `;

    static $styles: Style.Compat[] = [
      DefaultRow.$labelContainerStyle,
      css`
        :host {
          position: sticky;
          top: var(--sticky-top);
        }

        #root {
          position: relative;
          align-items: center;
          height: var(--label-height, 32px);
          padding-right: 8px;
        }

        #sync-indicator {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
        }

        #timescale-indicator {
          flex-shrink: 0;
          margin-left: auto;
          padding-left: 1px;
          visibility: hidden;
          /* Condensed projection: the D/W/M/Y badge is a hover-reveal detail
             with no room in a 64px lane. Note it already owns visibility
             (the hover rule below flips it), which is precisely why the
             collapsed state is expressed as opacity + a paired pointer token
             rather than as a visibility token — an inherited
             visibility: hidden would lose to that :host(:hover) rule and the
             badge would pop back over the condensed name. */
          opacity: var(--legend-row-badge-opacity, 1);
          pointer-events: var(--legend-row-badge-pointer, auto);
        }

        :host(:hover) #timescale-indicator,
        #timescale-indicator[visible] {
          visibility: visible;
        }
      `,
    ];

    static $html: HTMLTemplateElement = html`
      <std-row id="root">
        <std-row-icon id=icon emoji-target></std-row-icon>
        <std-row id="label-container" label-target>
          <p id="label" class="small"></p>
        </std-row>
      </std-row>
    `;
  }
}

export namespace SheetLegend {
  export class DefaultFactory {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }

    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
      sheetTimescale: Timescale,
    ): void {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
        { sheetTimescale },
      );
    }

    static computeWidth(
      row: SheetRow.Joined.Handle,
      measure: TextMeasurer,
      _sheetTimescale: string,
    ): number {
      const label = row.label ?? row.labelPlaceholder;
      // timescaleVisible=false because OVERHEAD(77) already includes the
      // badge(20px) — it's always in the DOM with visibility:hidden and
      // contributes to max-content width. Setting timescaleVisible:true
      // would double-count it.
      return DefaultLegendLayout.width({ label, timescaleVisible: false }, measure);
    }
  }
}
